/**
 * Project Overview — body component (no longer a tab).
 *
 * Mounted by `routes/ProjectEdit.tsx` at `/projects/:id/edit`,
 * opened from the project topbar's Edit button. Used to be a
 * tab inside ProjectDetail.tsx; promoted to its own page so
 * editing the project doesn't compete with the day-to-day tabs
 * (Transactions, Party, Site, …) for the tab row's real estate.
 *
 * The file lives under `projectDetail/` because it shares the
 * `ProjectListItem` prop shape with the other tab bodies and
 * keeps the project-scoped panels co-located.
 *
 * Layout:
 *   1. Sticky action bar — pinned at top while editing; shows
 *      "Unsaved changes · [Discard] [Save changes]" only when
 *      the draft diverges from the saved project doc.
 *   2. Editable project details — name, status, client, location,
 *      typology, value, start + end dates, single progress slider.
 *      Edits are buffered in a draft; the user commits with the
 *      Save / Discard buttons in the card header (or the sticky
 *      bar). Mirrors the mobile sheet pattern — no auto-save.
 *   3. Progress card — Task completion % + Budget utilisation %
 *      (2 columns). Read-only, computed from tasks + transactions.
 *   4. Danger zone — Delete project (cascade Cloud Function).
 *
 * Save errors surface in an inline red banner above the details
 * card. A transient "Saved ✓" badge flashes for ~2s on success.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { isAdminish } from '@shared/perms';

import {
  deleteProject,
  updateProject,
  type ProjectStatus,
  type ProjectTypology,
} from '@/lib/projects';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import type { ProjectListItem } from '@/lib/useProjects';
import { useProjectTasks } from '@/lib/useProjectTasks';
import {
  normaliseTxType,
  useProjectTransactions,
} from '@/lib/useProjectTransactions';

// ───── Helpers ─────

function fmtInr(n: number | undefined | null): string {
  if (!n || n <= 0) return '₹0';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)} L`;
  if (n >= 1000) return `₹${Math.round(n / 1000)}K`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}
function dateKeyFromTs(
  ts: { toMillis(): number } | null | undefined,
): string {
  if (!ts) return '';
  const d = new Date(ts.toMillis());
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function dateFromKey(k: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(k)) return null;
  const [y, m, d] = k.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

const STATUS_TONE: Record<
  ProjectStatus,
  { bg: string; fg: string; label: string }
> = {
  active:    { bg: '#DBEAFE', fg: '#1D4ED8', label: 'Active' },
  on_hold:   { bg: '#FEF3C7', fg: '#B45309', label: 'On hold' },
  completed: { bg: '#DCFCE7', fg: '#15803D', label: 'Completed' },
  archived:  { bg: '#F1F5F9', fg: '#475569', label: 'Archived' },
};

const TYPOLOGY_OPTIONS: { key: ProjectTypology; label: string }[] = [
  { key: 'residential',  label: 'Residential' },
  { key: 'commercial',   label: 'Commercial' },
  { key: 'hospitality',  label: 'Hospitality' },
  { key: 'industrial',   label: 'Industrial' },
  { key: 'other',        label: 'Other' },
];

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: '#fff',
  fontSize: 13,
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
  outline: 'none',
};
const FIELD_LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--text-tertiary)',
  marginBottom: 6,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

// Snapshot of the current project doc shaped into the draft
// state. Used both for initialising the draft and for the
// Discard / post-save reset.
function snapshotFromProject(project: ProjectListItem) {
  return {
    name: project.name ?? '',
    status: (project.status ?? 'active') as ProjectStatus,
    client: project.client ?? '',
    location: project.location ?? '',
    siteAddress: project.siteAddress ?? '',
    typology: (project.typology ?? 'residential') as ProjectTypology,
    value: project.value ?? 0,
    startKey: dateKeyFromTs(project.startDate),
    endKey: dateKeyFromTs(project.endDate),
    progress: project.progress ?? 0,
  };
}
type Draft = ReturnType<typeof snapshotFromProject>;

// ───── Component ─────

export function OverviewTab({ project }: { project: ProjectListItem }) {
  const navigate = useNavigate();
  const orgId = project.orgId ?? null;
  // Project delete is Super-Admin / Admin only per the role matrix
  // (`docs/roles-and-permissions.md` — "Project — delete: Y | Y | —
  // | — | …"). Firestore rule already enforces this; this guard
  // hides the Danger Zone card so Manager and below never see the
  // button + confirmation flow they'd be denied on tap.
  const { role: orgRole } = useCurrentOrgRole();
  const canDeleteProject = isAdminish(orgRole);

  // Two data sources — tasks (for completion %) and transactions
  // (for budget burn %). Everything else (attendance / material /
  // files / activity) was removed per the user's spec — those
  // numbers live on their own tabs.
  const { tasks } = useProjectTasks(orgId, project.id);
  const { transactions } = useProjectTransactions(orgId, project.id);

  // ── Editable draft + Save state ────────────────────────────
  const baseline = useMemo(() => snapshotFromProject(project), [project]);
  const [draft, setDraft] = useState<Draft>(baseline);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  // When the live project doc updates from Firestore (e.g. a
  // teammate edits the same project, or our own Save commits
  // and the snapshot fires back), re-sync the baseline. Only
  // overwrite the draft if the user hasn't started editing —
  // otherwise we'd clobber unsaved changes mid-typing.
  const userDirtyRef = useRef(false);
  useEffect(() => {
    if (!userDirtyRef.current) {
      setDraft(baseline);
    }
  }, [baseline]);

  // Wrap setDraft so any edit flips the userDirty flag. Discard
  // / Save reset the flag back to false.
  function editDraft(updater: (d: Draft) => Draft) {
    userDirtyRef.current = true;
    setDraft(updater);
  }

  // Pure diff against the saved doc — drives the enable state
  // on Save + Discard + the sticky bar's visibility.
  const isDirty = useMemo(() => {
    return (
      draft.name.trim() !== (baseline.name ?? '') ||
      draft.status !== baseline.status ||
      draft.client !== baseline.client ||
      draft.location !== baseline.location ||
      draft.siteAddress !== baseline.siteAddress ||
      draft.typology !== baseline.typology ||
      draft.value !== baseline.value ||
      draft.startKey !== baseline.startKey ||
      draft.endKey !== baseline.endKey ||
      draft.progress !== baseline.progress
    );
  }, [draft, baseline]);

  // ── Task summary (drives Task Completion KPI) ─────────────
  const taskSummary = useMemo(() => {
    let total = 0;
    let completed = 0;
    let sumProgress = 0;
    for (const t of tasks) {
      total += 1;
      sumProgress += t.progress ?? 0;
      if (t.status === 'completed') completed += 1;
    }
    const avgProgress = total > 0 ? Math.round(sumProgress / total) : 0;
    const effectiveProgress =
      typeof project.progress === 'number' ? project.progress : avgProgress;
    return { total, completed, avgProgress, effectiveProgress };
  }, [tasks, project.progress]);

  // ── Spend (drives Budget Utilisation KPI) ─────────────────
  const totalOut = useMemo(() => {
    let s = 0;
    for (const t of transactions) {
      if (normaliseTxType(t.type) === 'out') s += t.amount;
    }
    return s;
  }, [transactions]);

  const budgetPct =
    project.value && project.value > 0
      ? Math.min(100, Math.round((totalOut / project.value) * 100))
      : 0;
  const budgetTone =
    budgetPct >= 100
      ? 'var(--critical)'
      : budgetPct >= 80
        ? 'var(--warning)'
        : '#15803D';

  // ── Commit handlers ───────────────────────────────────────
  async function handleSave() {
    if (!isDirty || saving) return;
    if (!draft.name.trim()) {
      setSaveError('Project name is required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await updateProject({
        projectId: project.id,
        name: draft.name.trim(),
        status: draft.status,
        client: draft.client,
        location: draft.location,
        siteAddress: draft.siteAddress,
        typology: draft.typology,
        value: draft.value,
        startDate: dateFromKey(draft.startKey),
        endDate: dateFromKey(draft.endKey),
        progress: draft.progress,
      });
      // Saved — let the live snapshot re-sync the baseline on
      // its own. Clear the dirty flag so the snapshot effect can
      // overwrite the draft cleanly.
      userDirtyRef.current = false;
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 1800);
    } catch (e) {
      setSaveError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }
  function handleDiscard() {
    if (!isDirty || saving) return;
    userDirtyRef.current = false;
    setDraft(baseline);
    setSaveError(null);
  }

  // ── Delete-project flow ───────────────────────────────────
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function handleDelete() {
    if (deleteText.trim() !== project.name) {
      setDeleteError(
        `Type the project name exactly to confirm. Expected: "${project.name}"`,
      );
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteProject(project.id);
      navigate('/projects');
    } catch (e) {
      setDeleteError((e as Error).message);
      setDeleting(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* ─── Sticky save bar — only when dirty ─── */}
      {isDirty ? (
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 5,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 14px',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: 'var(--warning)',
              textTransform: 'uppercase',
              letterSpacing: 0.4,
            }}
          >
            <i className="ph ph-warning-circle" /> Unsaved changes
          </span>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="btn-secondary"
            onClick={handleDiscard}
            disabled={saving}
            style={{ fontSize: 12 }}
          >
            Discard
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSave}
            disabled={saving || !draft.name.trim()}
            style={{ fontSize: 12 }}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      ) : null}

      {saveError ? (
        <div
          className="card"
          style={{
            padding: 12,
            background: 'var(--critical-bg)',
            color: 'var(--critical)',
            border: '1px solid #FECACA',
            fontSize: 13,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>Couldn't save: {saveError}</span>
          <button
            type="button"
            className="btn-secondary"
            style={{ fontSize: 12 }}
            onClick={() => setSaveError(null)}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {/* ─── 1. Editable project details ─── */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div
          className="card-header"
          style={{
            background: '#F9FAFB',
            borderBottom: '1px solid var(--border)',
            gap: 10,
          }}
        >
          <div className="card-title" style={{ fontSize: 12 }}>
            PROJECT DETAILS
          </div>
          <div style={{ flex: 1 }} />
          {/* Single source for Save / Discard lives in the sticky
              bar at the top of the page (rendered above). We only
              keep the transient "Saved ✓" badge here so the user
              gets affirmative feedback right where their cursor
              is, without having to look up. */}
          {savedFlash ? (
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--success)',
                textTransform: 'uppercase',
                letterSpacing: 0.4,
              }}
            >
              <i className="ph-bold ph-check-circle" /> Saved
            </span>
          ) : null}
        </div>
        <div
          style={{
            padding: 20,
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 14,
          }}
        >
          <div style={{ gridColumn: 'span 2' }}>
            <label style={FIELD_LABEL}>Project name *</label>
            <input
              style={INPUT}
              value={draft.name}
              onChange={(e) => {
                // Capture the value BEFORE calling editDraft. React
                // nulls out `e.currentTarget` after the synchronous
                // handler returns, but our state-updater callback
                // can run later (and twice under StrictMode), so
                // reading `e.currentTarget.value` inside it crashes.
                // This pattern repeats on every input below.
                const v = e.currentTarget.value;
                editDraft((d) => ({ ...d, name: v }));
              }}
              maxLength={80}
              placeholder="Sharma Residence"
            />
          </div>
          <div>
            <label style={FIELD_LABEL}>Status</label>
            <select
              style={INPUT}
              value={draft.status}
              onChange={(e) => {
                const v = e.currentTarget.value as ProjectStatus;
                editDraft((d) => ({ ...d, status: v }));
              }}
            >
              {Object.entries(STATUS_TONE).map(([key, t]) => (
                <option key={key} value={key}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={FIELD_LABEL}>Client</label>
            <input
              style={INPUT}
              value={draft.client}
              onChange={(e) => {
                const v = e.currentTarget.value;
                editDraft((d) => ({ ...d, client: v }));
              }}
              placeholder="Client / family name"
            />
          </div>
          <div>
            <label style={FIELD_LABEL}>Location</label>
            <input
              style={INPUT}
              value={draft.location}
              onChange={(e) => {
                const v = e.currentTarget.value;
                editDraft((d) => ({ ...d, location: v }));
              }}
              placeholder="Area · City"
            />
          </div>
          <div>
            <label style={FIELD_LABEL}>Typology</label>
            <select
              style={INPUT}
              value={draft.typology}
              onChange={(e) => {
                const v = e.currentTarget.value as ProjectTypology;
                editDraft((d) => ({ ...d, typology: v }));
              }}
            >
              {TYPOLOGY_OPTIONS.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ gridColumn: 'span 3' }}>
            <label style={FIELD_LABEL}>Site address</label>
            <textarea
              style={{ ...INPUT, minHeight: 56, resize: 'vertical' }}
              value={draft.siteAddress}
              onChange={(e) => {
                const v = e.currentTarget.value;
                editDraft((d) => ({ ...d, siteAddress: v }));
              }}
              placeholder="Building / street / city / state / pincode"
            />
          </div>

          <div>
            <label style={FIELD_LABEL}>Start date</label>
            <input
              type="date"
              style={INPUT}
              value={draft.startKey}
              onChange={(e) => {
                const v = e.currentTarget.value;
                editDraft((d) => ({ ...d, startKey: v }));
              }}
            />
          </div>
          <div>
            <label style={FIELD_LABEL}>End date</label>
            <input
              type="date"
              style={INPUT}
              value={draft.endKey}
              min={draft.startKey || undefined}
              onChange={(e) => {
                const v = e.currentTarget.value;
                editDraft((d) => ({ ...d, endKey: v }));
              }}
            />
          </div>
          <div>
            <label style={FIELD_LABEL}>Contract value (₹)</label>
            <input
              type="number"
              style={{ ...INPUT, fontVariantNumeric: 'tabular-nums' }}
              value={draft.value || ''}
              onChange={(e) => {
                const raw = e.currentTarget.value;
                const num = parseFloat(raw) || 0;
                editDraft((d) => ({ ...d, value: num }));
              }}
              min={0}
              placeholder="0"
            />
          </div>

          {/* Single-row progress slider — mirrors the mobile edit
              screen exactly: label · slider · % display. The
              browser's native track (accentColor: var(--brand)) IS
              the visual fill, so no separate rendered progress bar
              is needed. */}
          <div style={{ gridColumn: 'span 3' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <label
                style={{
                  ...FIELD_LABEL,
                  marginBottom: 0,
                  minWidth: 88,
                  flexShrink: 0,
                }}
              >
                Progress
              </label>
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={draft.progress}
                onChange={(e) => {
                  const raw = e.currentTarget.value;
                  const pct = parseInt(raw, 10);
                  editDraft((d) => ({ ...d, progress: pct }));
                }}
                style={{
                  flex: 1,
                  accentColor: 'var(--brand)',
                  cursor: 'pointer',
                }}
              />
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  fontVariantNumeric: 'tabular-nums',
                  minWidth: 44,
                  textAlign: 'right',
                }}
              >
                {draft.progress}%
              </span>
            </div>
            <div
              style={{
                fontSize: 11,
                color: 'var(--text-tertiary)',
                marginTop: 6,
              }}
            >
              Leave at 0 to use the auto-computed task-average
              progress ({taskSummary.avgProgress}%) instead — that's
              what the topbar shows when no override is set.
            </div>
          </div>
        </div>
      </div>

      {/* ─── 2. Progress card ─── */}
      <div
        className="card"
        style={{
          padding: 20,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 24,
        }}
      >
        <ProgressMetric
          label="Task completion"
          percent={taskSummary.effectiveProgress}
          caption={
            taskSummary.total === 0
              ? 'No tasks yet — add some from the Timeline tab.'
              : `${taskSummary.completed} of ${taskSummary.total} tasks completed · ${taskSummary.avgProgress}% avg progress`
          }
          tone={
            taskSummary.effectiveProgress >= 100
              ? 'var(--success)'
              : taskSummary.effectiveProgress >= 70
                ? '#2563EB'
                : 'var(--brand)'
          }
        />
        <ProgressMetric
          label="Budget utilisation"
          percent={budgetPct}
          caption={
            project.value && project.value > 0
              ? `${fmtInr(totalOut)} of ${fmtInr(project.value)} spent · ${fmtInr(Math.max(0, project.value - totalOut))} left`
              : 'Set a contract value above to track budget burn.'
          }
          tone={budgetTone}
        />
      </div>

      {/* ─── 3. Danger zone ─── Super-Admin / Admin only. */}
      {canDeleteProject ? (
      <div
        className="card"
        style={{
          padding: 0,
          overflow: 'hidden',
          border: '1px solid #FECACA',
        }}
      >
        <div
          className="card-header"
          style={{
            background: '#FEF2F2',
            borderBottom: '1px solid #FECACA',
            color: 'var(--critical)',
          }}
        >
          <div
            className="card-title"
            style={{ fontSize: 12, color: 'var(--critical)' }}
          >
            DANGER ZONE
          </div>
        </div>
        <div style={{ padding: 18 }}>
          {!confirmDelete ? (
            <div
              style={{
                display: 'flex',
                gap: 12,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ flex: 1, minWidth: 240 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: 'var(--text-primary)',
                  }}
                >
                  Delete this project
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--text-secondary)',
                    marginTop: 2,
                  }}
                >
                  Permanently removes the project + every transaction,
                  task, DPR, attendance, material request, design, and
                  whiteboard scoped to it. R2 files are cleaned up
                  server-side. This cannot be undone.
                </div>
              </div>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setConfirmDelete(true)}
                style={{ color: 'var(--critical)' }}
              >
                <i className="ph ph-trash" /> Delete project…
              </button>
            </div>
          ) : (
            <div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: 'var(--critical)',
                  marginBottom: 8,
                }}
              >
                Type the project name to confirm deletion
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                  marginBottom: 12,
                }}
              >
                Expected: <code>{project.name}</code>
              </div>
              <input
                style={{
                  ...INPUT,
                  borderColor: '#FECACA',
                  marginBottom: 12,
                }}
                value={deleteText}
                onChange={(e) => setDeleteText(e.currentTarget.value)}
                placeholder={project.name}
                autoFocus
              />
              {deleteError ? (
                <div
                  style={{
                    padding: 10,
                    background: '#FEF2F2',
                    border: '1px solid #FECACA',
                    borderRadius: 6,
                    color: 'var(--critical)',
                    fontSize: 13,
                    marginBottom: 12,
                  }}
                >
                  {deleteError}
                </div>
              ) : null}
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  justifyContent: 'flex-end',
                }}
              >
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setConfirmDelete(false);
                    setDeleteText('');
                    setDeleteError(null);
                  }}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleDelete}
                  disabled={deleting || deleteText.trim() !== project.name}
                  style={{
                    background: 'var(--critical)',
                    borderColor: 'var(--critical)',
                  }}
                >
                  {deleting ? 'Deleting…' : 'Yes, delete forever'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      ) : null}
    </div>
  );
}

// ───── Progress KPI ─────

function ProgressMetric({
  label,
  percent,
  caption,
  tone,
}: {
  label: string;
  percent: number;
  caption: string;
  tone: string;
}) {
  const safePct = Math.max(0, Math.min(100, percent));
  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--text-tertiary)',
            letterSpacing: 0.5,
            textTransform: 'uppercase',
          }}
        >
          {label}
        </span>
        <span
          style={{
            fontSize: 20,
            fontWeight: 800,
            color: tone,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {safePct}%
        </span>
      </div>
      <div
        style={{
          height: 8,
          background: '#F1F5F9',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${safePct}%`,
            height: '100%',
            background: tone,
            transition: 'width 0.25s',
          }}
        />
      </div>
      <div
        style={{
          fontSize: 12,
          color: 'var(--text-secondary)',
          marginTop: 8,
          lineHeight: 1.4,
        }}
      >
        {caption}
      </div>
    </div>
  );
}
